import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Logo } from "./Logo";
import { CONTACT } from "../data";

const LINKS = [
  { label: "About", href: "#about" },
  { label: "Services", href: "#services" },
  { label: "Gallery", href: "#gallery" },
  { label: "Film", href: "#film" },
  { label: "Reviews", href: "#reviews" },
  { label: "Contact", href: "#contact" },
];

export const Navbar = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
      data-testid="main-navbar"
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,border-color,padding] duration-500 ${
        scrolled
          ? "border-b border-[#E6E2D6] bg-[#FCFBF8]/80 py-3 backdrop-blur-xl"
          : "border-b border-transparent py-5"
      }`}
    >
      <nav className="mx-auto flex max-w-[1500px] items-center justify-between px-6 sm:px-12 lg:px-16">
        <Logo />

        <div className="hidden items-center gap-9 lg:flex">
          {LINKS.map((l) => (
            <a
              key={l.href}
              href={l.href}
              data-testid={`nav-link-${l.label.toLowerCase()}`}
              className="group relative font-body text-sm font-medium text-[#1A1918]/80 transition-colors duration-300 hover:text-[#1A1918]"
            >
              {l.label}
              <span className="absolute -bottom-1 left-0 h-px w-0 bg-[#C5A059] transition-[width] duration-300 group-hover:w-full" />
            </a>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <a
            href={CONTACT.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="nav-lets-talk"
            className="hidden rounded-full bg-[#1A1918] px-6 py-3 font-body text-sm font-medium text-[#FCFBF8] transition-colors duration-300 hover:bg-[#C5A059] hover:text-[#0D0C0A] sm:inline-block"
          >
            Let&apos;s Talk
          </a>

          {/* Mobile dropdown (shadcn) */}
          <div className="lg:hidden">
            <DropdownMenu open={open} onOpenChange={setOpen}>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="mobile-menu-trigger"
                  aria-label="Open menu"
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-[#E6E2D6] bg-[#FCFBF8] text-[#1A1918]"
                >
                  {open ? <X size={18} /> : <Menu size={18} />}
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                data-testid="mobile-menu-content"
                className="w-56 rounded-2xl border-[#E6E2D6] bg-[#FCFBF8]/95 backdrop-blur-xl"
              >
                {LINKS.map((l) => (
                  <DropdownMenuItem key={l.href} asChild>
                    <a
                      href={l.href}
                      data-testid={`mobile-nav-${l.label.toLowerCase()}`}
                      className="cursor-pointer font-body text-sm"
                    >
                      {l.label}
                    </a>
                  </DropdownMenuItem>
                ))}
                <DropdownMenuItem asChild>
                  <a
                    href={CONTACT.whatsapp}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="cursor-pointer font-body text-sm font-semibold text-[#A37E3A]"
                  >
                    WhatsApp Us
                  </a>
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        </div>
      </nav>
    </motion.header>
  );
};
